import React, { useState } from 'react';
import apiClient from '../../Services/ApiClient';
import Loading from '../Loading';
import { useEffectAsync } from '../../helpers';
import { useAppContext } from '../../Services/AppContext';
import PatientUserRelation from './PatientUserRelation';
import PatientUserRelationAdd from './PatientUserRelationAdd';
import FirstUpper from '../FirstUpper';
import v2styles from '../PatientProfile/PatientProfileV2.module.scss';
import {
	useLazyPatientUserRelationsQuery,
	UserRelation,
} from '../../graphql/graphql';
import useLoading from '../../Modules/Base/Hooks/useLoading';

interface Props {
	userId: string;
	v2template: boolean;
	userRelations: UserRelation[];
}

const PatientUserRelations: React.FC<Props> = ({
	userId,
	v2template = false,
	userRelations: defaultUserRelations = [],
}) => {
	const [userRelations, setUserRelations] =
		useState<UserRelation[]>(defaultUserRelations);
	const [adding, setAdding] = useState(false);
	const { translation } = useAppContext();
	const [getPatientUserRelations] = useLazyPatientUserRelationsQuery();
	const { isLoading, setIsLoading } = useLoading();

	const handleRemove = async (id: string) => {
		await apiClient.delete('/user-relations/' + id);
		await loadData();
	};

	const handleReload = async () => {
		await loadData();
		setAdding(false);
	};

	useEffectAsync(async () => {
		if (!defaultUserRelations) {
			await loadData();
		}
	}, []);

	const loadData = async () => {
		setIsLoading(true);
		if (userId) {
			const result = await getPatientUserRelations({
				userId,
			}).unwrap();
			setUserRelations((result?.user?.userRelations ?? []) as UserRelation[]);
		}
		setIsLoading(false);
	};

	//FILTER TYPES OF RELATIONS
	const filteredUserRelations = userRelations.filter(
		item => item.type && [3, 4, 1].includes(item.type),
	);

	if (isLoading) {
		return (
			<div className={'py-5'}>
				<Loading />
			</div>
		);
	}

	if (v2template) {
		return (
			<div className={v2styles.doctors}>
				<div className={v2styles.title}>
					<FirstUpper>
						{translation['doctor.patientCardScreen.doctors']}
					</FirstUpper>
				</div>
				{filteredUserRelations.map(item => {
					return (
						<PatientUserRelation
							key={item.id}
							userRelation={item}
							handleRemove={handleRemove}
							v2template={true}
						/>
					);
				})}
				{adding && (
					<PatientUserRelationAdd
						userId={userId}
						handleReload={handleReload}
						v2template={true}
					/>
				)}
				{!adding && (
					<a
						onClick={() => setAdding(true)}
						className='patient-profile__panel__content__attending-doctors__add-button'>
						<FirstUpper>
							{
								translation[
									'component.patientProfile.addNextAmbualnceAndDoctor'
								]
							}
						</FirstUpper>
					</a>
				)}
			</div>
		);
	}

	return (
		<div className='patient-profile__panel__content__attending-doctors'>
			<div className='patient-profile__panel__content__attending-doctors__title'>
				{translation['doctor.patientCardScreen.doctors']}
			</div>
			{filteredUserRelations.map(userRelation => (
				<PatientUserRelation
					key={userRelation.id}
					userRelation={userRelation}
					handleRemove={handleRemove}
				/>
			))}
			{adding && (
				<PatientUserRelationAdd userId={userId} handleReload={handleReload} />
			)}
			{!adding && (
				<a
					onClick={() => setAdding(true)}
					className='patient-profile__panel__content__attending-doctors__add-button'>
					<FirstUpper>
						{translation['component.patientProfile.addNextAmbualnceAndDoctor']}
					</FirstUpper>
				</a>
			)}
		</div>
	);
};

export default PatientUserRelations;
